import { ArrowRight } from "lucide-react";
import { Link } from "react-router";
import { isOpenTask, issueKey, MOOD_TEXT, type TaskMood, taskMood } from "../../lib/tasks";
import type { AgentSummary, FactoryLayout, FactoryState, Task } from "../../types";
import { MoodIcon } from "../mood-icon";
import { ColorDot, StatusDot } from "../status-badge";
import { Button } from "../ui/button";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

interface Placed {
  task: Task;
  mood: TaskMood;
}

const SHOWN = 4;

/** Kanban-style view of the floor: one column per section, each station with the work orders parked at it. */
export function ProductionBoard({ layout, state }: { layout: FactoryLayout; state: FactoryState }) {
  const runsById = new Map(state.runs.map((r) => [r.id, r]));
  const mergeByTask = new Map(state.mergeQueue.map((m) => [m.taskId, m]));
  const byAgent = new Map<string, Placed[]>();
  const intake: Placed[] = [];
  for (const task of state.tasks) {
    if (!isOpenTask(task)) continue;
    const run = task.currentRunId ? runsById.get(task.currentRunId) : undefined;
    const placed = { task, mood: taskMood(task, run, mergeByTask.get(task.id)) };
    const agentId = task.currentAgentId ?? run?.agentId;
    if (!agentId) intake.push(placed);
    else byAgent.set(agentId, [...(byAgent.get(agentId) ?? []), placed]);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Production line</CardTitle>
        <CardDescription>Where every open work order is right now, station by station</CardDescription>
        <CardAction>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/tasks">
              All work orders <ArrowRight />
            </Link>
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="space-y-3">
        {intake.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed px-3 py-2 text-sm">
            <span className="font-medium">Waiting at intake</span>
            {intake.map((p) => (
              <TaskChip key={p.task.id} placed={p} />
            ))}
          </div>
        )}
        <div className="grid gap-3 @3xl/main:grid-cols-3 @7xl/main:grid-cols-6">
          {layout.sections.map((section) => {
            const count = section.agents.reduce((n, a) => n + (byAgent.get(a.id)?.length ?? 0), 0);
            return (
              <section key={section.id} className="flex flex-col gap-2 rounded-lg bg-muted/40 p-2.5">
                <header className="flex items-center gap-2 px-1 pt-0.5">
                  <ColorDot color={section.color} />
                  <h3 className="truncate text-xs font-semibold">{section.name}</h3>
                  <span className="ml-auto text-xs text-muted-foreground tabular-nums">{count}</span>
                </header>
                {section.agents.map((agent) => (
                  <StationCard
                    key={agent.id}
                    agent={agent}
                    stats={state.stations[agent.id]}
                    tasks={byAgent.get(agent.id) ?? []}
                  />
                ))}
              </section>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function StationCard({
  agent,
  stats,
  tasks,
}: {
  agent: AgentSummary;
  stats: FactoryState["stations"][string] | undefined;
  tasks: Placed[];
}) {
  const active = stats?.active ?? 0;
  const queued = stats?.queued ?? 0;
  const blocked = stats?.blocked ?? 0;
  const tone = blocked > 0 ? "amber" : active > 0 ? "sky" : "neutral";
  // Lifts a touch on hover, the way a card you could pick up would.
  return (
    <div className="rounded-md border bg-card p-2.5 shadow-xs transition-[transform,box-shadow] duration-200 ease-calm hover:-translate-y-px hover:shadow-sm motion-reduce:transform-none">
      <div className="flex items-center gap-2">
        <StatusDot tone={tone} pulse={active > 0} />
        <span className="truncate text-sm font-medium">{agent.name}</span>
        <span className="ml-auto shrink-0 text-xs text-muted-foreground tabular-nums">
          {blocked > 0
            ? `${blocked} blocked`
            : active > 0
              ? `${active} active`
              : queued > 0
                ? `${queued} queued`
                : "Idle"}
        </span>
      </div>
      {tasks.length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {tasks.slice(0, SHOWN).map((p) => (
            <li key={p.task.id}>
              <TaskRow placed={p} />
            </li>
          ))}
          {tasks.length > SHOWN && (
            <li className="px-1.5 pt-0.5 text-xs text-muted-foreground">+{tasks.length - SHOWN} more</li>
          )}
        </ul>
      )}
    </div>
  );
}

function TaskRow({ placed }: { placed: Placed }) {
  const key = issueKey(placed.task);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to={`/tasks/${placed.task.id}`}
          className="flex items-center gap-2 rounded px-1.5 py-1 text-xs hover:bg-muted"
        >
          <MoodIcon mood={placed.mood} />
          {key && <span className="shrink-0 font-mono text-muted-foreground">{key}</span>}
          <span className="truncate">{placed.task.title}</span>
        </Link>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-64">
        <p className="font-medium">{placed.task.title}</p>
        <p className="opacity-80">{MOOD_TEXT[placed.mood]}</p>
      </TooltipContent>
    </Tooltip>
  );
}

function TaskChip({ placed }: { placed: Placed }) {
  return (
    <Link
      to={`/tasks/${placed.task.id}`}
      className="inline-flex max-w-64 items-center gap-1.5 rounded-md border bg-card px-2 py-0.5 text-xs hover:bg-muted"
    >
      <MoodIcon mood={placed.mood} />
      <span className="truncate">{issueKey(placed.task) ?? placed.task.title}</span>
    </Link>
  );
}
